import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser'; // Import EmailJS
import Footer from './components/Footer';
import Navbar from './components/Navbar';
import Industry from './images/Industrial-Visit.png';

// Custom hook for getting current window width
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const resize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  return width;
}

const IndustrialVisitProgram = () => {
  const width = useWindowWidth();

  // Define breakpoints
  const isMobile = width <= 600;
  const isTablet = width > 600 && width < 1080;
  const isDesktop = width >= 1080;

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    institutionName: '',
    studentCount: '',
    preferredDate: '',
    industryPreference: '',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const styles = {
    // Root container responsive
    container: {
      minHeight: '100vh',
      background: '#fff',
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      color: '#1a1a1a'
    },

    // Hero Section
    heroSection: {
      background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 50%, #ff8c42 100%)',
      padding: isMobile ? '56px 6px 32px' : (isTablet ? '80px 20px' : '120px 20px'),
      position: 'relative',
      overflow: 'hidden'
    },
    heroContent: {
      maxWidth: isMobile ? '98vw' : '1200px',
      margin: '0 auto',
      display: isMobile ? 'flex' : 'grid',
      flexDirection: isMobile ? 'column' : undefined,
      gridTemplateColumns: isMobile ? undefined : '1fr 1fr',
      gap: isMobile ? '24px' : '60px',
      alignItems: 'center'
    },
    heroText: {
      color: 'white',
      width: isMobile ? '100%' : undefined,
      textAlign: isMobile ? 'center' : 'left',
      marginBottom: isMobile ? '24px' : 0
    },
    badge: {
      backgroundColor: 'rgba(255,255,255,0.2)',
      border: '1px solid rgba(255,255,255,0.3)',
      borderRadius: '25px',
      padding: isMobile ? '7px 12px' : '8px 16px',
      fontSize: isMobile ? '0.95rem' : '0.9rem',
      fontWeight: 500,
      marginBottom: '24px',
      color: 'white',
      display: 'inline-block'
    },
    heroTitle: {
      fontSize: isMobile ? '2.1rem' : (isTablet ? '2.7rem' : '3.5rem'),
      fontWeight: 800,
      lineHeight: 1.15,
      marginBottom: '20px',
      color: 'white'
    },
    heroSubtitle: {
      fontSize: isMobile ? '1rem' : '1.2rem',
      color: 'rgba(255,255,255,0.9)',
      lineHeight: 1.6,
      marginBottom: '24px'
    },
    requestButton: {
      background: 'rgba(255,255,255,0.15)',
      border: '2px solid rgba(255,255,255,0.3)',
      color: 'white',
      padding: isMobile ? '10px 18px' : '14px 32px',
      borderRadius: '9px',
      fontSize: isMobile ? '0.96rem' : '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s',
      backdropFilter: 'blur(10px)'
    },
    heroImage: {
      display: 'flex',
      justifyContent: isMobile ? 'center' : undefined,
      alignItems: 'center',
      width: isMobile ? '100%' : undefined
    },
    visitImage: {
      width: isMobile ? '88vw' : '400px',
      height: isMobile ? '250px' : '500px',
      backgroundColor: '#2a2a2a',
      borderRadius: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 14px 34px rgba(0,0,0,0.18)'
    },

    // Why Visits
    whyVisitsSection: {
      padding: isMobile ? '36px 6px' : '80px 20px',
      backgroundColor: '#fff'
    },
    whyVisitsContent: {
      maxWidth: isMobile ? '98vw' : '1200px',
      margin: '0 auto'
    },
    whyVisitsBadge: {
      backgroundColor: '#fff3e0',
      color: '#ff6b35',
      border: '1px solid #ffcc80',
      borderRadius: '25px',
      padding: isMobile ? '7px 12px' : '8px 16px',
      fontSize: isMobile ? '0.95rem' : '0.9rem',
      fontWeight: 500,
      marginBottom: '24px',
      display: 'inline-block'
    },
    whyVisitsTitle: {
      fontSize: isMobile ? '1.6rem' : '2.5rem',
      fontWeight: 700,
      color: '#1a1a1a',
      marginBottom: isMobile ? '36px' : '60px',
      textAlign: isMobile ? 'center' : 'left'
    },
    whyVisitsGrid: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '30px' : '80px',
      alignItems: 'center',
      marginBottom: isMobile ? '40px' : '80px'
    },
    whyVisitsText: {
      fontSize: isMobile ? '0.98rem' : '1.1rem',
      lineHeight: 1.8,
      color: '#333',
      marginBottom: isMobile ? '18px' : '32px',
      textAlign: isMobile ? 'center' : 'left'
    },

    // Features
    featuresSection: {
      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%)',
      padding: isMobile ? '38px 6px' : '100px 20px',
      color: 'white'
    },
    featuresContent: {
      maxWidth: isMobile ? '98vw' : '1200px',
      margin: '0 auto',
      textAlign: 'center'
    },
    featuresTitle: {
      fontSize: isMobile ? '1.3rem' : '2.5rem',
      fontWeight: 700,
      marginBottom: isMobile ? '16px' : '60px',
      color: 'white'
    },
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr 1fr' : 'repeat(auto-fit, minmax(280px, 1fr))'),
      gap: isMobile ? '18px' : '40px'
    },
    featureCard: {
      backgroundColor: 'rgba(255,255,255,0.12)',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255,255,255,0.22)',
      borderRadius: '15px',
      padding: isMobile ? '22px' : '32px',
      textAlign: 'center',
      transition: 'all 0.3s'
    },
    featureIcon: {
      width: '52px',
      height: '52px',
      backgroundColor: 'rgba(255,255,255,0.16)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      fontSize: isMobile ? '1.5em' : '1.95em'
    },
    featureTitle: {
      fontSize: isMobile ? '1.09rem' : '1.3rem',
      fontWeight: 600,
      marginBottom: '12px',
      color: 'white'
    },
    featureDescription: {
      fontSize: isMobile ? '0.96rem' : '0.98rem',
      color: 'rgba(255,255,255,0.88)',
      lineHeight: 1.5
    },

    // Contact Form Section
    contactSection: {
      padding: isMobile ? '38px 4px' : '100px 20px',
      backgroundColor: '#f8fafc'
    },
    contactContent: {
      maxWidth: isMobile ? '99vw' : '800px',
      margin: '0 auto'
    },
    contactTitle: {
      fontSize: isMobile ? '1.3rem' : '2.5rem',
      fontWeight: 700,
      textAlign: 'center',
      marginBottom: '24px',
      color: '#1a1a1a'
    },
    contactSubtitle: {
      fontSize: isMobile ? '1rem' : '1.2rem',
      color: '#64748b',
      textAlign: 'center',
      marginBottom: isMobile ? '32px' : '60px',
      lineHeight: 1.6
    },
    formCard: {
      backgroundColor: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: '24px',
      padding: isMobile ? '18px 7px' : '40px',
      boxShadow: '0 10px 25px rgba(0,0,0,0.06)'
    },
    formRow: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '0px' : '20px',
      marginBottom: isMobile ? '0px' : '24px'
    },
    formGroup: {
      flex: 1,
      marginBottom: isMobile ? '20px' : '24px'
    },
    label: {
      display: 'block',
      fontSize: isMobile ? '0.97rem' : '0.99rem',
      fontWeight: 600,
      color: '#374151',
      marginBottom: isMobile ? '5px' : '8px'
    },
    input: {
      width: '100%',
      padding: isMobile ? '10px' : '14px 16px',
      backgroundColor: '#fff',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: isMobile ? '0.97rem' : '1rem',
      color: '#1e293b',
      outline: 'none',
      transition: 'all 0.3s',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: isMobile ? '10px' : '14px 16px',
      backgroundColor: '#fff',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: isMobile ? '0.97rem' : '1rem',
      color: '#1e293b',
      outline: 'none',
      transition: 'all 0.3s',
      boxSizing: 'border-box',
      cursor: 'pointer'
    },
    textarea: {
      width: '100%',
      padding: isMobile ? '10px' : '14px 16px',
      backgroundColor: '#fff',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: isMobile ? '0.97rem' : '1rem',
      color: '#1e293b',
      outline: 'none',
      transition: 'all 0.3s',
      resize: 'vertical',
      minHeight: isMobile ? '70px' : '120px',
      fontFamily: 'inherit',
      boxSizing: 'border-box'
    },
    submitBtn: {
      width: '100%',
      padding: isMobile ? '13px' : '16px 24px',
      background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: isMobile ? '1.01rem' : '1.1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s',
      boxShadow: '0 8px 32px rgba(255,107,53,0.18)'
    }
  };

  const features = [
    {
      icon: '🏭',
      title: 'Industry Exposure',
      description: 'Visit leading companies and see AI applications in real-world industrial environments.'
    },
    {
      icon: '👥',
      title: 'Expert Interactions',
      description: 'Meet industry professionals, engineers, and data scientists working on cutting-edge AI projects.'
    },
    {
      icon: '🔬',
      title: 'Live Demonstrations',
      description: 'Witness AI systems in action and understand their practical implementations.'
    },
    {
      icon: '💡',
      title: 'Innovation Insights',
      description: 'Learn about latest AI trends, challenges, and breakthrough technologies.'
    },
    {
      icon: '🎯',
      title: 'Career Guidance',
      description: 'Get valuable career advice and understand skill requirements for AI roles.'
    },
    {
      icon: '🤝',
      title: 'Networking Opportunities',
      description: 'Build connections with industry professionals and potential mentors.'
    }
  ];

  // Event Handlers
  const handleRequestVisit = () => {
    const formSection = document.getElementById('contact-form');
    if (formSection) {
        formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email || !formData.institutionName || !formData.studentCount) {
        alert("Please fill out all required fields.");
        return;
    }
    setIsSubmitting(true);

    const serviceID = 'service_n36e8hg';
    const templateID = 'template_i9jkfri';
    const publicKey = 'VAEdCMXuUKeCOw6gb';

    const emailBody = `
      New Industrial Visit Request
      -----------------------------

      Requester Details:
      - Full Name: ${formData.fullName}
      - Email: ${formData.email}
      - Institution: ${formData.institutionName}
      
      Visit Details:
      - Number of Students: ${formData.studentCount}
      
      Additional Requirements:
      ${formData.requirements || 'No specific requirements were provided.'}
    `;
    
    const templateParams = {
        email_subject: `Industrial Visit Request from ${formData.institutionName}`,
        email_body: emailBody.trim()
    };

    emailjs.send(serviceID, templateID, templateParams, publicKey)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        alert('Thank you for your request! We have received your details and will get back to you soon to coordinate the visit.');
        setIsSubmitting(false);
        setFormData({
            fullName: '', email: '', institutionName: '', studentCount: '',
            preferredDate: '', industryPreference: '', requirements: ''
        });
      })
      .catch((err) => {
        console.error('FAILED...', err);
        alert('There was an error submitting your request. Please try again later or contact us directly.');
        setIsSubmitting(false);
      });
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleInputFocus = (e) => {
    e.target.style.border = '2px solid #ff6b35';
    e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)';
  };
  const handleInputBlur = (e) => {
    e.target.style.border = '2px solid #e2e8f0';
    e.target.style.boxShadow = 'none';
  };
  const handleCardHover = (e) => {
    e.currentTarget.style.transform = 'translateY(-6px)';
    e.currentTarget.style.boxShadow = '0 14px 34px rgba(255,107,53,0.16)';
  };
  const handleCardLeave = (e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = 'none';
  };
  const handleButtonHover = (e) => {
    e.target.style.backgroundColor = 'rgba(255,255,255,0.28)';
    e.target.style.transform = 'translateY(-2px)';
  };
  const handleButtonLeave = (e) => {
    e.target.style.backgroundColor = 'rgba(255,255,255,0.15)';
    e.target.style.transform = 'none';
  };
  const handleSubmitHover = (e) => {
    if (e.target.disabled) return;
    e.target.style.transform = 'translateY(-2px)';
    e.target.style.boxShadow = '0 12px 40px rgba(255, 107, 53, 0.27)';
  };
  const handleSubmitLeave = (e) => {
    if (e.target.disabled) return;
    e.target.style.transform = 'translateY(0)';
    e.target.style.boxShadow = '0 8px 32px rgba(255, 107, 53, 0.13)';
  };

  // --- Render
  return (
    <div>
      <Navbar />
      <div style={styles.container}>
        {/* Hero Section */}
        <div style={styles.heroSection}>
          <div style={styles.heroContent}>
            <div style={styles.heroText}>
              <div style={styles.badge}>Industrial Visits</div>
              <h1 style={styles.heroTitle}>
                Experience AI in<br />Real Industries
              </h1>
              <p style={styles.heroSubtitle}>
                Bridge theory and practice with immersive industrial experiences.
              </p>
              <button
                style={styles.requestButton}
                onClick={handleRequestVisit}
                onMouseEnter={handleButtonHover}
                onMouseLeave={handleButtonLeave}
                onTouchStart={handleButtonHover}
                onTouchEnd={handleButtonLeave}
              >
                Industrial Workshops
              </button>
            </div>
          </div>
        </div>

        {/* Why Industrial Visits Section */}
        <div style={styles.whyVisitsSection}>
          <div style={styles.whyVisitsContent}>
            <div style={styles.whyVisitsBadge}>Learning</div>
            <h2 style={styles.whyVisitsTitle}>Why Industrial Visits</h2>
            <div style={styles.whyVisitsGrid}>
              <div style={{ flex: 1 }}>
                <p style={styles.whyVisitsText}>
                  Industrial visits serve as a crucial link between academic knowledge and real-world applications. They provide students with the unique opportunity to step outside the classroom and observe firsthand how theoretical concepts, particularly in the field of Artificial Intelligence (AI), are brought to life in practical, industrial settings. These visits allow learners to witness live demonstrations of AI technologies, understand how different industries integrate AI into their operations, and explore the decision-making processes behind AI-driven innovations.
                </p>
              </div>
              {!isMobile &&
                <div style={{ flex: 1 }}>
                  <img
                    src={Industry}
                    alt="Students in Industry"
                    style={{ height: isTablet ? '18em' : '25em', borderRadius: '20px', width: '100%', objectFit: 'cover' }}
                  />
                </div>
              }
            </div>
            {/* Show image below paragraph on mobile */}
            {isMobile && (
              <div style={{ textAlign: 'center', marginBottom: 0 }}>
                <img
                  src={Industry}
                  alt="Students in Industry"
                  style={{ height: '13em', width: '95vw', borderRadius: '18px', objectFit: 'cover', marginTop: '12px' }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Features Section */}
        <div style={styles.featuresSection}>
          <div style={styles.featuresContent}>
            <h2 style={styles.featuresTitle}>
              Comprehensive Learning,<br />Real-World Exposure
            </h2>
            <p style={{
              fontSize: isMobile ? '0.96rem' : '1.1rem',
              color: 'rgba(255,255,255,0.89)',
              marginBottom: isMobile ? '16px' : '60px'
            }}>
              Designed to provide maximum learning value through structured industry interactions
            </p>
            <div style={styles.featuresGrid}>
              {features.map((feature, idx) => (
                <div
                  key={idx}
                  style={styles.featureCard}
                  onMouseEnter={handleCardHover}
                  onMouseLeave={handleCardLeave}
                  onTouchStart={handleCardHover}
                  onTouchEnd={handleCardLeave}
                >
                  <div style={styles.featureIcon}>{feature.icon}</div>
                  <div style={styles.featureTitle}>{feature.title}</div>
                  <div style={styles.featureDescription}>{feature.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Form Section */}
        <div id="contact-form" style={styles.contactSection}>
          <div style={styles.contactContent}>
            <h2 style={styles.contactTitle}>Book Your Industrial Visit</h2>
            <p style={styles.contactSubtitle}>
              Ready to provide your students with real-world AI exposure? Fill out the form below
              to schedule an industrial visit tailored to your educational goals.
            </p>
            <div style={styles.formCard}>
              <form onSubmit={handleFormSubmit} autoComplete="off">
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Full Name *</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={styles.input}
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={styles.input}
                      placeholder="your.email@institution.edu"
                      required
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Institution Name *</label>
                    <input
                      type="text"
                      name="institutionName"
                      value={formData.institutionName}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={styles.input}
                      placeholder="Your institution name"
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Number of Students *</label>
                    <select
                      name="studentCount"
                      value={formData.studentCount}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      style={styles.select}
                      required
                    >
                      <option value="">Select group size</option>
                      <option value="10-25">10-25 students</option>
                      <option value="26-50">26-50 students</option>
                      <option value="51-100">51-100 students</option>
                      <option value="100+">100+ students</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Additional Requirements</label>
                  <textarea
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    style={styles.textarea}
                    placeholder="Tell us about specific learning objectives, accessibility needs, or any other requirements for your visit..."
                  />
                </div>

                <button
                  type="submit"
                  style={styles.submitBtn}
                  onMouseEnter={handleSubmitHover}
                  onMouseLeave={handleSubmitLeave}
                  onTouchStart={handleSubmitHover}
                  onTouchEnd={handleSubmitLeave}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Visit Request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default IndustrialVisitProgram;
